import { getOpfsPathForManifestFile } from "@/install/install-flow";
import type { LaneManifest } from "@/runtime/events";
import { readOpfsFile, readOpfsText } from "@/storage/opfs";

interface ManifestAssetIndex {
  relativeToOpfsPath: Map<string, string>;
  relativePaths: string[];
}

const manifestIndexCache = new Map<string, ManifestAssetIndex>();

function normalizePath(value: string): string {
  return value.replace(/\\/g, "/").replace(/^\/+/, "");
}

function inferContentType(relativePath: string): string {
  if (relativePath.endsWith(".json")) {
    return "application/json";
  }

  if (relativePath.endsWith(".txt")) {
    return "text/plain; charset=utf-8";
  }

  if (relativePath.endsWith(".wasm")) {
    return "application/wasm";
  }

  if (relativePath.endsWith(".onnx") || relativePath.endsWith(".data") || relativePath.endsWith(".gguf")) {
    return "application/octet-stream";
  }

  if (relativePath.endsWith(".model")) {
    return "application/octet-stream";
  }

  return "application/octet-stream";
}

function getManifestRelativePath(manifest: LaneManifest, sourcePath: string): string {
  const normalized = normalizePath(sourcePath);
  const marker = `${manifest.id}/`;
  const markerIndex = normalized.indexOf(marker);

  if (markerIndex >= 0) {
    return normalized.slice(markerIndex + marker.length);
  }

  const fileName = normalized.split("/").pop();

  if (!fileName) {
    throw new Error(`Invalid manifest file path: ${sourcePath}`);
  }

  return fileName;
}

function getManifestIndex(manifest: LaneManifest): ManifestAssetIndex {
  const cached = manifestIndexCache.get(manifest.id);

  if (cached) {
    return cached;
  }

  const relativeToOpfsPath = new Map<string, string>();
  const relativePaths: string[] = [];

  manifest.files.forEach((file) => {
    const relativePath = normalizePath(getManifestRelativePath(manifest, file.path));
    const opfsPath = getOpfsPathForManifestFile(manifest, file.path);
    relativeToOpfsPath.set(relativePath, opfsPath);
    relativePaths.push(relativePath);
  });

  const index = {
    relativeToOpfsPath,
    relativePaths,
  };
  manifestIndexCache.set(manifest.id, index);
  return index;
}

function getAliasCandidates(normalizedPath: string): string[] {
  const aliases = new Set<string>();

  if (normalizedPath.endsWith("processor_config.json")) {
    aliases.add(normalizedPath.replace(/processor_config\.json$/, "preprocessor_config.json"));
  }

  if (normalizedPath.endsWith("preprocessor_config.json")) {
    aliases.add(normalizedPath.replace(/preprocessor_config\.json$/, "processor_config.json"));
  }

  return [...aliases];
}

export function resolveManifestAssetOpfsPath(
  manifest: LaneManifest,
  requestedPath: string,
): string | null {
  const normalized = normalizePath(requestedPath);
  const index = getManifestIndex(manifest);
  const candidates = [normalized, ...getAliasCandidates(normalized)];

  for (const candidate of candidates) {
    const direct = index.relativeToOpfsPath.get(candidate);
    if (direct) {
      return direct;
    }
  }

  // Path-boundary suffix match: require the manifest entry to match either
  // the full normalized candidate or to appear after a `/` separator.
  // Previously this used a bare `endsWith`, which meant a request for
  // `.../preprocessor_config.json` would incorrectly match the manifest
  // entry `config.json` (because the request string ends with the substring
  // "config.json"). That returned the main model config instead of the
  // preprocessor config and surfaced as a Transformers.js error
  // (`No 'image_processor_type' or 'feature_extractor_type' found in the
  // config.`) inside AutoProcessor.from_pretrained. The bench could not
  // reproduce it because the earlier Whisper manifest happened to order its
  // files such that the intended match came first; Moonshine's tighter file
  // set exposed the bug on iPhone during the first mic turn.
  const matchesPath = (candidate: string, relativePath: string): boolean =>
    candidate === relativePath ||
    candidate.endsWith(`/${relativePath}`) ||
    relativePath === candidate ||
    relativePath.endsWith(`/${candidate}`);

  const bySuffix = index.relativePaths.find((relativePath) =>
    candidates.some((candidate) => matchesPath(candidate, relativePath)),
  );

  if (bySuffix) {
    return index.relativeToOpfsPath.get(bySuffix) ?? null;
  }

  const requestedFileNames = candidates
    .map((candidate) => candidate.split("/").pop())
    .filter((value): value is string => Boolean(value));

  const byFileName = index.relativePaths.find((relativePath) =>
    requestedFileNames.includes(relativePath.split("/").pop() ?? ""),
  );

  return byFileName ? index.relativeToOpfsPath.get(byFileName) ?? null : null;
}

export async function readManifestAssetFile(
  manifest: LaneManifest,
  requestedPath: string,
): Promise<File> {
  const opfsPath = resolveManifestAssetOpfsPath(manifest, requestedPath);

  if (!opfsPath) {
    throw new Error(`Asset not installed for ${manifest.id}: ${requestedPath}`);
  }

  return readOpfsFile(opfsPath);
}

export async function readManifestAssetText(
  manifest: LaneManifest,
  requestedPath: string,
): Promise<string> {
  const opfsPath = resolveManifestAssetOpfsPath(manifest, requestedPath);

  if (!opfsPath) {
    throw new Error(`Asset not installed for ${manifest.id}: ${requestedPath}`);
  }

  return readOpfsText(opfsPath);
}

export async function matchManifestAssetRequest(
  manifest: LaneManifest,
  request: string,
): Promise<Response | undefined> {
  const candidate = String(request);

  let pathname = candidate;
  const origin = typeof location !== "undefined" ? location.origin : "http://localhost";

  try {
    pathname = new URL(candidate, origin).pathname;
  } catch {
    pathname = candidate;
  }

  const relativePath = normalizePath(pathname);
  const opfsPath = resolveManifestAssetOpfsPath(manifest, relativePath);

  if (!opfsPath) {
    return undefined;
  }

  const file = await readOpfsFile(opfsPath);

  return new Response(file, {
    headers: {
      "content-length": String(file.size),
      "content-type": inferContentType(relativePath),
    },
  });
}

export function createManifestAssetReader(manifest: LaneManifest): {
  readBuffer: (input: { relativePath: string }) => Promise<Uint8Array>;
  readJson: (input: { relativePath: string }) => Promise<unknown>;
  readText: (input: { relativePath: string }) => Promise<string>;
} {
  return {
    async readBuffer({ relativePath }) {
      return new Uint8Array(await (await readManifestAssetFile(manifest, relativePath)).arrayBuffer());
    },
    async readJson({ relativePath }) {
      return JSON.parse(await readManifestAssetText(manifest, relativePath));
    },
    async readText({ relativePath }) {
      return readManifestAssetText(manifest, relativePath);
    },
  };
}
